<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <div class="title-head">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t('payroll.email_batches') }} - {{ payrollRun.name }}
          </oxd-text>
        </div>
        <div>
          <oxd-button
            :label="$t('payroll.start_email_batches')"
            icon-name="envelope"
            display-type="secondary"
            @click="onClickStart"
          />
        </div>
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
      <div class="orangehrm-button-row">
        <oxd-button
          display-type="ghost"
          :label="$t('general.back')"
          @click="onCancel"
        />
      </div>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {APIService} from '@ohrm/core/util/services/api.service';
import moment from 'moment';
import {onMounted, ref} from 'vue';
import {navigate} from '@/core/util/helper/navigation';

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },
  props: {
    payrollRunId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const payrollRun = ref({name: ''});
    const more = {
      component: 'oxd-table-dropdown',
      props: {
        options: [],
        style: {'margin-left': 'auto'},
        onClick: null,
      },
    };

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/email-batches',
    );

    const payrollRuns = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/payroll/runs/${props.payrollRunId}`,
    );

    onMounted(async () => {
      const response = await payrollRuns.get();
      const {data} = response.data;
      payrollRun.value.name = data.name;
    });

    const formatTime = (time) => {
      if (!time) return '';
      const utcTime = moment.utc(time, 'YYYY-MM-DD HH:mm:ss.SSSSSS');
      const localTime = utcTime.clone().tz(moment.tz.guess());
      return localTime.format('YYYY-MM-DD HH:mm:ss');
    };

    const dataNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          status: item.status,
          total: item.total,
          processed: item.processed,
          succeeded: item.succeeded,
          failed: item.failed,
          startAt: formatTime(item.startedAt?.date),
          finishAt: formatTime(item.finishedAt?.date),
        };
      });
    };
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      prefetch: false,
      normalizer: dataNormalizer,
      query: {runId: props.payrollRunId},
    });

    execQuery(false);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      payrollRun,
      more,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'status',
          title: this.$t('general.status'),
          style: {flex: 1, 'text-transform': 'capitalize'},
        },
        {
          name: 'total',
          title: this.$t('general.total'),
          style: {flex: 1},
        },
        {
          name: 'processed',
          title: this.$t('payroll.processed'),
          style: {flex: 1},
        },
        {
          name: 'succeeded',
          title: this.$t('payroll.succeeded'),
          style: {flex: 1},
        },
        {
          name: 'failed',
          title: this.$t('payroll.failed'),
          style: {flex: 1},
        },
        {
          name: 'startAt',
          title: this.$t('payroll.started_at'),
          style: {flex: 1},
        },
        {
          name: 'finishAt',
          title: this.$t('payroll.finished_at'),
          style: {flex: 1},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1, 'text-align': 'center'},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
      batchPollTimer: null,
    };
  },
  beforeUnmount() {
    this.stopBatchPoll();
  },
  mounted() {
    this.$nextTick(() => {
      const rows = this.items?.data ?? [];
      if (
        rows.some((row) =>
          ['queued', 'processing'].includes((row.status || '').toLowerCase()),
        )
      ) {
        this.startBatchPoll();
      }
    });
  },

  methods: {
    actionRenderer(...[, , , row]) {
      const dropdownActions = [];
      const cellConfig = {};
      cellConfig.delete = {
        onClick: () => this.onClickDelete(row),
        component: 'oxd-icon-button',
        props: {
          name: 'trash',
        },
      };
      dropdownActions.push({
        label: 'Retry',
        context: 'retry',
      });
      dropdownActions.push({
        label: 'Abort',
        context: 'abort',
      });
      if (dropdownActions.length > 0) {
        this.more.props.options = dropdownActions;
        this.more.props.onClick = ($event) =>
          this.onDropdownAction($event, row);
        cellConfig.more = this.more;
      }
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onCancel() {
      navigate(`/payroll/viewPayrollRuns`);
    },
    onClickStart() {
      const fetch = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/email-batches?runId=${this.payrollRunId}`,
      );
      this.isLoading = true;
      fetch
        .create({})
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.resetDataTable();
          this.startBatchPoll();
        })
        .catch((response) => {
          this.$toast.error({
            title: this.$t('general.warning'),
            message: response?.response.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onDropdownAction(event, item) {
      if (event.context === 'retry') {
        this.onUpdateEmailBatch(item.id, 'retry');
      } else {
        this.onUpdateEmailBatch(item.id, 'abort');
      }
    },
    onUpdateEmailBatch(id, method) {
      const fetch = new APIService(
        window.appGlobal.baseUrl,
        `/api/v2/payroll/email-batches/${id}?action=${method}`,
      );
      this.isLoading = true;
      fetch
        .update()
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.resetDataTable();
          if (method === 'retry') {
            this.startBatchPoll();
          }
        })
        .catch((response) => {
          this.$toast.error({
            title: this.$t('general.warning'),
            message: response?.response.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery(false);
    },
    startBatchPoll() {
      this.stopBatchPoll();
      this.batchPollTimer = setInterval(() => {
        this.pollBatchStatus();
      }, 3000);
      this.pollBatchStatus();
    },
    stopBatchPoll() {
      if (this.batchPollTimer) {
        clearInterval(this.batchPollTimer);
        this.batchPollTimer = null;
      }
    },
    async pollBatchStatus() {
      await this.execQuery(false);
      const rows = this.items?.data ?? [];
      const active = rows.some((row) =>
        ['queued', 'processing'].includes((row.status || '').toLowerCase()),
      );
      if (!active) {
        this.stopBatchPoll();
      }
    },
  },
};
</script>
<style scoped>
.orangehrm-button-row {
  padding: 20px;
  text-align: left;
}
</style>
